import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

function DashboardStats() {
  const [stats, setStats] = useState({
    loads: { total: 0, pending: 0, inProgress: 0, complete: 0 },
    trucks: { total: 0, active: 0 },
    drivers: { total: 0, available: 0 }
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const ACTIVE_STATUSES = ['assigned', 'en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading', 'pending_completion'];   // __STATUS_TRUTH__ Confirm Complete is still open work
      const DONE_STATUSES = ['complete', 'ready_to_bill', 'billed'];

      // Loads: exact head-counts — count:'exact', head:true returns the server's
      // true total via the Content-Range header and transfers ZERO rows, so these
      // numbers are never subject to the 1000-row page cap that a select().length
      // would silently hit above 1000 loads.
      const countLoads = async (apply) => {
        let q = supabase.from('loads').select('id', { count: 'exact', head: true });
        if (apply) q = apply(q);
        const { count, error } = await q;
        if (error) throw error;
        return count || 0;
      };

      const [total, pending, inProgress, complete] = await Promise.all([
        countLoads(null),
        countLoads((q) => q.eq('status', 'pending')),
        countLoads((q) => q.in('status', ACTIVE_STATUSES)),
        countLoads((q) => q.in('status', DONE_STATUSES)),
      ]);

      const loadStats = { total, pending, inProgress, complete };

      // Drivers busy on an active load — scoped to active statuses only, so this
      // row read is inherently fleet-bounded (a driver can be on very few loads
      // at once) and can't approach the page cap. Used to derive availability.
      const { data: activeLoads, error: activeErr } = await supabase
        .from('loads')
        .select('driver_id')
        .in('status', ACTIVE_STATUSES)
        .not('driver_id', 'is', null);
      if (activeErr) throw activeErr;
      const busyDriverIds = new Set((activeLoads || []).map(l => l.driver_id));

      // Trucks — small table; is_active flag (no status column)
      const { data: trucks, error: trucksError } = await supabase
        .from('trucks')
        .select('id, is_active');
      if (trucksError) throw trucksError;

      const truckStats = {
        total: trucks?.length || 0,
        active: trucks?.filter(t => t.is_active).length || 0
      };

      // Drivers — small table
      const { data: drivers, error: driversError } = await supabase
        .from('drivers')
        .select('id');
      if (driversError) throw driversError;

      const driverStats = {
        total: drivers?.length || 0,
        available: drivers?.filter(d => !busyDriverIds.has(d.id)).length || 0
      };

      setStats({ loads: loadStats, trucks: truckStats, drivers: driverStats });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();

    const sub = supabase
      .channel('dashboard-stats')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'loads' }, fetchStats)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trucks' }, fetchStats)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'drivers' }, fetchStats)
      .subscribe();

    return () => supabase.removeChannel(sub);
  }, [fetchStats]);

  const StatCard = ({ title, value, subtitle, color }) => (
    <div style={{
      backgroundColor: 'var(--surface-1)',
      padding: '1rem',
      borderRadius: 'var(--radius-md)',
      textAlign: 'center',
      minWidth: '120px',
      border: '1px solid var(--border-default)'
    }}>
      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: color || 'var(--accent-primary)', marginBottom: '0.25rem' }}>
        {loading ? '-' : value}
      </div>
      <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '0.25rem' }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{subtitle}</div>
      )}
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
      marginBottom: '1.5rem',
      paddingBottom: '1.5rem',
      borderBottom: '1px solid var(--border-default)'
    }}>
      <StatCard
        title="Total Loads"
        value={stats.loads.total}
        subtitle={`${stats.loads.pending} pending`}
        color="var(--accent-primary)"
      />
      <StatCard
        title="Active"
        value={stats.loads.inProgress}
        subtitle="In progress"
        color="var(--accent-blue)"
      />
      <StatCard
        title="Completed"
        value={stats.loads.complete}
        subtitle="Done / billed"
        color="var(--color-success)"
      />
      <StatCard
        title="Trucks"
        value={stats.trucks.total}
        subtitle={`${stats.trucks.active} active`}
        color="var(--color-warning)"
      />
      <StatCard
        title="Drivers"
        value={stats.drivers.total}
        subtitle={`${stats.drivers.available} available`}
        color="var(--color-info)"
      />
    </div>
  );
}

export default DashboardStats;
